import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { EmployeeModel } from "../models/employee.model";
import { Observable, Subscription } from 'rxjs';
import { Store } from "@ngrx/store";
import * as fromApp from "../store/app.reducer";
import { CustomerModel } from "../models/customer.model";
import * as AuthActions from '../auth/store/auth.actions';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { InviteCoworkersModalComponent } from '../shared/components/invite-coworkers-modal/invite-coworkers-modal.component';
import { MobileSidenavContentComponent } from '../shared/mobile-sidenav-content/mobile-sidenav-content.component';
import { ScrollHandlerService } from './assignments/create-assignment/step3/ScrollHandler.service';
import { AssignmentStatisticsModel } from '../models/assignment.statistics.model';
import * as CompanyActions from './company/store/company.actions';
import { SubscribeService } from '../services/subscribe.service';
import { TranslateService } from '@ngx-translate/core';
import { PusherService } from '../service/pusher.service';
import { PosthogService } from '../service/posthog.service';
import { MsalService } from '@azure/msal-angular';
import { AiChatService, AiChatStreamEvent } from '../services/ai-chat.service';

@Component({
    selector: 'app-components',
    templateUrl: './components.component.html',
    styleUrls: ['./components.component.scss'],
    standalone: false
})
export class ComponentsComponent implements OnInit, OnDestroy {

    public employee: EmployeeModel;
    public customers: CustomerModel[];
    storeSub: Subscription;
    private routerSub: Subscription;
    public opened: boolean = true;
    public innerWidth: any;
    public mode: string = "side";
    public closeIcon: boolean = false;
    public navButton: boolean = false;
    public customer: CustomerModel;
    public switchAccountOptions: boolean = false;
    public overview: boolean = false;
    public company: boolean = false;
    public messages: boolean = false;
    public reports: boolean = false;
    public assignments: boolean = false;
    public dynamicMonitoring: boolean = false;
    public qualityModes: boolean = false;
    public createAssignment: boolean = false;
    public isCollapsed: boolean = false;
    messagesCount$: Observable<number>;
    public searchForm: UntypedFormGroup;
    public filteredCustomers: CustomerModel[];
    public assignmentsStatistics: AssignmentStatisticsModel;
    isSwitchRotated = false;
    public showNavigationButton: boolean = true;
    public showScrollToTop: boolean = false;
    private scrollThreshold: number = 100; // Hide button after scrolling 100px
    languageDropdownOpen = false;
    headerCompanyDropdownOpen = false;
    headerSearchTerm: string = '';
    headerFilteredCustomers: CustomerModel[] = [];
    public useAiSidebar: boolean = true;
    public aiInput: string = '';
    /** Translation keys for initial suggestion buttons when no assistant suggestions exist yet */
    public readonly aiSuggestionKeys: string[] = ['ai.suggestion1', 'ai.suggestion2', 'ai.suggestion3'];
    public aiConversation: {
        role: 'assistant' | 'user';
        message: string;
        messageKey?: string;
        provider?: string;
        sources?: unknown[];
        suggestions?: string[];
        sentAt?: Date;
        /** Set while waiting for tokens: thinking (network) → generating (after SSE start). */
        thinkingState?: 'thinking' | 'generating';
    }[] = [
            { role: 'assistant', message: '', messageKey: 'ai.welcome', suggestions: this.aiSuggestionKeys, sentAt: new Date() }
        ];
    public aiSending = false;
    /** True while the backend is regenerating suggestion chips (OpenAI via API). */
    public aiSuggestionsRefreshing = false;
    public aiError: string | null = null;
    private aiConversationLoaded = false;
    private aiStreamSubscription?: Subscription;
    private translateLangSub?: Subscription;
    private suggestionRegenerateSub?: Subscription;
    private aiStreamingMessageIndex?: number;
    @ViewChild('headerCompanySearchInput') headerCompanySearchInput?: ElementRef<HTMLInputElement>;
    @ViewChild('aiScrollContent') aiScrollContent?: ElementRef<HTMLDivElement>;

    constructor(
        public router: Router,
        private store: Store<fromApp.AppState>,
        private formBuilder: UntypedFormBuilder,
        private dialog: MatDialog,
        private scrollService: ScrollHandlerService,
        private cdr: ChangeDetectorRef,
        private subscriberService: SubscribeService,
        private translate: TranslateService,
        private pusherService: PusherService,
        private posthogService: PosthogService,
        private msalService: MsalService,
        private aiChatService: AiChatService
    ) {
        this.searchForm = this.formBuilder.group({
            searchCustomer: ['']
        });

        // Subscribe to search form changes
        this.searchForm.get('searchCustomer').valueChanges.subscribe(value => {
            this.filterCustomers(value);
        });
    }

    // Helper method to check if we're on the onboarding page
    get isOnboardingPage(): boolean {
        let status: boolean = false;
        if (this.router.url.includes('/components/assignments/create-assignment')) {
            status = true;
        } else if (this.router.url === '/components/on-boarding') {
            status = true;
        }
        return status;
    }

    // Helper method to determine if sidebar should be shown (both when logged in and when guest; hidden only on onboarding)
    get showSidebar(): boolean {
        return !this.isOnboardingPage;
    }

    private closeSwitchAccountOptions(): void {
        this.switchAccountOptions = false;
    }

    ngOnInit(): void {
        this.subscriberService.closeSidebarSubject.subscribe(() => {
            this.opened = false;
        });
        setTimeout(() => {
            this.onResize();
            this.cdr.detectChanges();
        }, 200);
        // Subscribe to router events to close switch account options on navigation
        this.routerSub = this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.closeSwitchAccountOptions();
            }
        });
        this.storeSub = this.store.subscribe(appState => {
            this.employee = appState?.auth?.employee;
            this.customers = appState?.auth?.employee?.customers;
            this.customer = appState?.auth?.customer;
            // Initialize filtered customers with all customers
            this.filteredCustomers = this.customers;
            this.headerFilteredCustomers = this.customers;
            this.assignmentsStatistics = appState?.assignments?.assignmentStatistics;
        });

        // Handle window resize
        this.innerWidth = window.innerWidth;
        if (this.innerWidth <= 800) {
            this.mode = "over";
            this.closeIcon = true;
        } else {
            this.mode = "side";
            this.closeIcon = false;
        }

        // Set opened state based on screen size and onboarding page
        if (this.innerWidth <= 768) {
            this.opened = false; // Default closed for mobile
            this.isCollapsed = false; // Always expanded on mobile

        } else {
            this.opened = !this.isOnboardingPage && !this.customer; // Desktop behavior remains same
            this.isCollapsed = false; // Start expanded by default on desktop

        }
        if (this.router.url.includes("components/company") || this.router.url.includes("components/company/users")) {
            this.company = true;
        }
        if (this.router.url.includes("components/reports")) {
            this.reports = true;
        }
        if (this.router.url.includes("assignments")) {
            this.assignments = true;
        }
        if (this.router.url.includes("create-assignment")) {
            this.createAssignment = true;
        }
        if (this.router.url.includes("messages")) {
            this.messages = true;
        }
        if (this.router.url.includes("dynamic-monitoring")) {
            this.dynamicMonitoring = true;
        }
        if (this.router.url.includes("quality-modes")) {
            this.qualityModes = true;
        }

        // Add search functionality
        this.searchForm.get('searchCustomer').valueChanges.subscribe(searchTerm => {
            if (!searchTerm) {
                this.filteredCustomers = this.customers;
                return;
            }
            searchTerm = searchTerm.toLowerCase();
            this.filteredCustomers = this.customers.filter(customer =>
                customer.companyName.toLowerCase().includes(searchTerm)
            );
        });

        // Load AI conversation history from API (same session_id for guest or employee)
        this.loadAiConversation();

        this.translateLangSub = this.translate.onLangChange.subscribe((e) => {
            this.regenerateAiSuggestionsForLocale(e.lang);
        });
        setTimeout(() => {
            const lang =
                localStorage.getItem('locale') || this.translate.currentLang || 'sv';
            this.regenerateAiSuggestionsForLocale(lang.split('-')[0]);
        }, 0);
    }

    loadAiConversation(): void {
        if (this.aiConversationLoaded) {
            return;
        }
        this.aiConversationLoaded = true;
        if (!this.aiChatService.hasKnownConversation()) {
            return;
        }
        const sessionId = this.aiChatService.getOrCreateSessionId();
        this.aiChatService.getConversation(sessionId).subscribe((res) => {
            if (res.success && res.data?.messages?.length) {
                this.aiConversation = res.data.messages.map((m) => ({
                    role: m.role as 'assistant' | 'user',
                    message: m.content,
                    messageKey: undefined,
                    provider: m.llm_provider,
                    sources: m.document_references as unknown[] | undefined,
                    sentAt: m.created_at ? new Date(m.created_at) : undefined
                }));
                this.cdr.detectChanges();
                this.scrollAiChatToBottom();
            }
        });
    }

    onMainScroll(event: Event): void {
        const target = event.target as HTMLElement;
        const scrollTop = target.scrollTop;
        this.scrollService.handleScroll(scrollTop);

        // Hide navigation button when scrolling down, show when at top
        this.showNavigationButton = scrollTop < this.scrollThreshold;

        // Show scroll to top button when scrolled down
        this.showScrollToTop = scrollTop > this.scrollThreshold;
    }

    scrollToTop(): void {
        const sidenavContent = document.querySelector('mat-sidenav-content');
        if (sidenavContent) {
            sidenavContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    ngOnDestroy(): void {
        if (this.storeSub) {
            this.storeSub.unsubscribe();
        }
        if (this.routerSub) {
            this.routerSub.unsubscribe();
        }
        if (this.aiStreamSubscription) {
            this.aiStreamSubscription.unsubscribe();
        }
        if (this.translateLangSub) {
            this.translateLangSub.unsubscribe();
        }
        if (this.suggestionRegenerateSub) {
            this.suggestionRegenerateSub.unsubscribe();
        }
    }

    // Helper method to close sidebar on mobile navigation
    private handleMobileNavigation(): void {
        if (this.innerWidth <= 768) {
            this.opened = false;

        }
    }

    private handleUnauthenticated(): void {
        this.store.dispatch(new AuthActions.Logout());
        localStorage.removeItem('jwt');
        this.router.navigate(['/components/assignments']);
    }

    onReportsClicked(): void {
        if (!localStorage.getItem('jwt')) {
            this.handleUnauthenticated();
            return;
        }
        this.handleMobileNavigation();
        this.setActiveNavItem('reports');
    }

    onDynamicMonitoringClicked(): void {
        if (!localStorage.getItem('jwt')) {
            this.handleUnauthenticated();
            return;
        }
        this.handleMobileNavigation();
        this.setActiveNavItem('dynamicMonitoring');
    }

    onQualityModesClicked(): void {
        if (!localStorage.getItem('jwt')) {
            this.handleUnauthenticated();
            return;
        }
        this.handleMobileNavigation();
        this.setActiveNavItem('qualityModes');
    }

    onAssignmentsClicked(): void {
        if (!localStorage.getItem('jwt')) {
            this.handleUnauthenticated();
            return;
        }
        this.setActiveNavItem('assignments');
        this.handleMobileNavigation();
        this.router.navigate(['/components/assignments']);
    }

    onMessagesClicked(): void {
        if (!localStorage.getItem('jwt')) {
            this.handleUnauthenticated();
            return;
        }
        this.handleMobileNavigation();
        this.setActiveNavItem('messages');
    }

    // Helper method to filter customers based on search input
    private filterCustomers(searchValue: string): void {
        if (!searchValue) {
            this.filteredCustomers = this.customers;
            return;
        }
        searchValue = searchValue.toLowerCase();
        this.filteredCustomers = this.customers?.filter(customer =>
            customer.companyName.toLowerCase().includes(searchValue)
        );
    }

    onResize() {
        this.innerWidth = window.innerWidth;
        if (this.innerWidth <= 768) {
            this.mode = "over";
            this.closeIcon = true;
            this.navButton = true;
            this.isCollapsed = false; // Always expanded on mobile
        } else {
            this.mode = "side";
            this.closeIcon = false;
            this.navButton = false;
            this.opened = true; // Always open for desktop
        }
    }

    toggleSidebar() {
        // this.opened = !this.opened;
        if (this.innerWidth > 768) {
            // On desktop, toggle collapse state instead of closing sidebar
            if (this.opened) {
                this.isCollapsed = !this.isCollapsed;
            }
        } else {
            // On mobile, toggle sidebar open/close as before
            this.opened = !this.opened;

        }
    }

    // Handle click outside to show sidebar
    @HostListener('document:click', ['$event'])
    handleClickOutside(event: Event) {
        const target = event.target as HTMLElement;
        const companyDropdownWrapper = document.querySelector('.top-header-company');
        const languageDropdownWrapper = document.querySelector('.top-header-lang');
        const switchAccountWrapper = document.querySelector('.switch-account-options');
        const sidebarToggleButton = document.querySelector('.sidebar-toggle-button');
        const sidebar = document.querySelector('mat-sidenav');

        // Close top header company options menu on outside click
        if (this.headerCompanyDropdownOpen &&
            companyDropdownWrapper &&
            !companyDropdownWrapper.contains(target)) {
            this.closeHeaderDropdown();
        }

        // Close language dropdown on outside click
        if (this.languageDropdownOpen &&
            languageDropdownWrapper &&
            !languageDropdownWrapper.contains(target)) {
            this.languageDropdownOpen = false;
        }

        // Close switch account popup on outside click
        if (this.switchAccountOptions &&
            switchAccountWrapper &&
            !switchAccountWrapper.contains(target)) {
            this.switchAccountOptions = false;
        }

        // If clicking outside both sidebar and toggle button, and sidebar is closed, open it
        if (!this.opened &&
            sidebar && !sidebar.contains(target) &&
            sidebarToggleButton && !sidebarToggleButton.contains(target)) {
            this.opened = true;

        }
    }

    @HostListener('document:keydown.escape')
    handleEscapeKey(): void {
        if (this.headerCompanyDropdownOpen) {
            this.closeHeaderDropdown();
        }
        if (this.languageDropdownOpen) {
            this.languageDropdownOpen = false;
        }
    }

    onCreateAssignment(): void {
        if (!localStorage.getItem('jwt')) {
            this.handleUnauthenticated();
            return;
        }
        this.handleMobileNavigation();
        this.setActiveNavItem('createAssignment');
        this.router.navigate(['/components/assignments/create-assignment/step1']);
    }

    public isCurrentCompany(customer: CustomerModel): boolean {
        return localStorage.getItem('customer') === customer?.id.toString();
    }

    onSwitchCustomer(customer: CustomerModel) {
        if (this.isCurrentCompany(customer)) {
            return;
        }

        // Save selected customer
        localStorage.setItem('customer', customer.id.toString());
        this.store.dispatch(new AuthActions.SwitchCompanyAccount({ customer }));
        this.switchAccountOptions = false;

        // Load employees
        this.store.dispatch(new CompanyActions.LoadEmployees({
            customerId: this.customer.id,
        }));

        const assignmentsUrl = '/components/assignments';

        if (this.router.url === assignmentsUrl) {
            // Already on assignments page, reload to fetch new data
            this.subscriberService.loadPage(true)
            // window.location.reload();
        } else {
            // Navigate normally
            this.router.navigate([assignmentsUrl]);
            if (this.innerWidth <= 768) {
                this.opened = false;
            }
            // this.opened = false;
        }
        this.searchForm.get('searchCustomer')?.setValue('');
        this.handleMobileNavigation();
    }

    onInviteCoworkers() {
        this.dialog.open(InviteCoworkersModalComponent, {
            data: {
                employee: this.employee,
                customer: this.customer
            }
        });
    }

    onSwitchAccountClicked() {
        // On mobile, just open the sidebar instead of directly opening the modal
        if (this.innerWidth <= 768) {
            this.opened = true;

        }
    }

    onCompanyNameClicked(event?: Event) {
        if (this.customers?.length <= 1) {
            this.router.navigate(['/components/company/users']);
            // this.opened = false;
            return;
        }

        // Prevent event propagation to avoid triggering parent click handlers
        if (event) {
            event.stopPropagation();
        }

        if (this.innerWidth <= 768) {
            this.opened = false;
        }


        this.setActiveNavItem('company');
        this.router.navigate(['/components/company/users']);
    }

    switchCompany() {
        // this.switchAccountOptions = !this.switchAccountOptions;
        if (this.innerWidth <= 768) {
            this.dialog.open(MobileSidenavContentComponent, {
                data: {
                    employee: this.employee,
                    customer: this.customer
                },
                hasBackdrop: false,
                panelClass: 'mobile-sidenav-dialog'
            });

        } else {
            // On desktop, toggle the switch account options
            this.switchAccountOptions = !this.switchAccountOptions;
        }
    }

    async onLogout(): Promise<void> {
        try {
            this.pusherService.disconnect();
            this.posthogService.reset();
            this.store.dispatch(new AuthActions.Logout());

            sessionStorage.clear();
            localStorage.clear();

            if (this.msalService.instance.getActiveAccount()) {
                this.msalService.instance.setActiveAccount(null);
            }

            Promise.all([
                navigator.serviceWorker?.getRegistrations()
                    .catch(err => console.error('SW cleanup error:', err)),
                'caches' in window
                    ? caches.keys()
                        .then(cacheNames => Promise.all(cacheNames.map(name => caches.delete(name))))
                        .catch(err => console.error('Cache cleanup error:', err))
                    : Promise.resolve()
            ]).catch(err => console.error('Cleanup error:', err));
            window.location.href = '/components/assignments';
        } catch (error) {
            console.error('Error during logout:', error);
            window.location.href = '/components/assignments';
        }
    }

    openHeaderDropdown(): void {
        this.headerCompanyDropdownOpen = !this.headerCompanyDropdownOpen;
        if (this.headerCompanyDropdownOpen) {
            this.languageDropdownOpen = false;
            this.headerSearchTerm = '';
            this.headerFilteredCustomers = this.customers ?? [];
            // Focus search input after dropdown is rendered
            setTimeout(() => {
                this.headerCompanySearchInput?.nativeElement?.focus();
            }, 0);
        }
    }

    /** Toggles language menu; closes company dropdown so the two never overlap. */
    toggleLanguageDropdown(): void {
        this.languageDropdownOpen = !this.languageDropdownOpen;
        if (this.languageDropdownOpen) {
            this.closeHeaderDropdown();
        }
    }

    onHeaderSearchChange(value: string): void {
        this.headerSearchTerm = value;
        if (!value) {
            this.headerFilteredCustomers = this.customers ?? [];
            return;
        }
        const term = value.toLowerCase();
        this.headerFilteredCustomers = (this.customers ?? []).filter(c =>
            c.companyName.toLowerCase().includes(term)
        );

        setTimeout(() => {
            if (this.headerCompanySearchInput) {
                this.headerCompanySearchInput.nativeElement.focus();
            }
        }, 0);
    }

    clearHeaderSearch(): void {
        this.headerSearchTerm = '';
        this.headerFilteredCustomers = this.customers ?? [];
    }

    closeHeaderDropdown(): void {
        this.headerCompanyDropdownOpen = false;
        this.headerSearchTerm = '';
        this.headerFilteredCustomers = this.customers ?? [];
    }

    setLanguage(lang: string, event?: Event): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        localStorage.setItem('locale', lang);
        this.languageDropdownOpen = false;

        if (this.employee?.id) {
            this.store.dispatch(new AuthActions.UpdateProfile({
                employeeId: this.employee.id,
                locale: lang
            }));
        }

        this.translate.use(lang);
        this.cdr.detectChanges();
    }

    /** @deprecated kept for guest language switcher backward compat */
    setLanguageForGuest(lang: string, event?: Event): void {
        this.setLanguage(lang, event);
    }

    get currentLang(): string {
        return this.translate.currentLang || localStorage.getItem('locale') || 'sv';
    }

    get currentLangCode(): string {
        const lang = localStorage.getItem('locale') || this.translate.currentLang || 'sv';
        return lang === 'en' ? 'EN' : 'SV';
    }

    onImageError(event: Event): void {
        const imgElement = event.target as HTMLImageElement;
        imgElement.src = '../../assets/img/safetly_logo_re.png';
    }

    private setActiveNavItem(activeItem: string): void {
        this.overview = false;
        this.company = false;
        this.messages = false;
        this.reports = false;
        this.assignments = false;
        this.createAssignment = false;
        this.dynamicMonitoring = false;
        this.qualityModes = false;

        switch (activeItem) {
            case 'assignments':
                this.assignments = true;
                break;
            case 'createAssignment':
                this.createAssignment = true;
                break;
            case 'company':
                this.company = true;
                break;
            case 'reports':
                this.reports = true;
                break;
            case 'dynamicMonitoring':
                this.dynamicMonitoring = true;
                break;
            case 'qualityModes':
                this.qualityModes = true;
                break;
            case 'messages':
                this.messages = true;
                break;
            default:
                this.overview = true;
        }
    }

    get isCompanyRouteActive(): boolean {
        const url = this.router.url;
        return url === '/company' || url === '/company/users';
    }

    clearSearch() {
        this.searchForm.get('searchCustomer')?.setValue('');
        this.filteredCustomers = this.customers; // restore full list
    }

    /** Current suggestions to show: last assistant message's suggestions from API, or default translation keys. */
    get currentAiSuggestions(): string[] {
        const lastAssistant = [...this.aiConversation].reverse().find(m => m.role === 'assistant');
        return (lastAssistant?.suggestions?.length ? lastAssistant.suggestions : this.aiSuggestionKeys) ?? [];
    }

    /** Display label for a suggestion: translate if it's a known i18n key, otherwise show API text as-is. */
    getSuggestionLabel(suggestion: string): string {
        return this.aiSuggestionKeys.includes(suggestion) ? this.translate.instant(suggestion) : suggestion;
    }

    useSuggestion(suggestion: string): void {
        this.aiInput = this.getSuggestionLabel(suggestion);
        this.sendAiMessage();
    }

    /**
     * When the UI language changes, ask the backend to regenerate follow-up suggestions (OpenAI on the server).
     * Skips when the only chips are static i18n keys. No-op if the endpoint is missing or returns an error.
     */
    private regenerateAiSuggestionsForLocale(locale: string): void {
        const target = locale.split('-')[0];
        const lastAssistant = [...this.aiConversation].reverse().find((m) => m.role === 'assistant');
        if (!lastAssistant?.suggestions?.length) {
            return;
        }
        const hasOnlyKeys = lastAssistant.suggestions.every((s) => this.aiSuggestionKeys.includes(s));
        if (hasOnlyKeys) {
            return;
        }
        if (this.aiSending) {
            return;
        }

        this.suggestionRegenerateSub?.unsubscribe();
        this.aiSuggestionsRefreshing = true;
        const customerId = this.customer?.id ?? null;
        this.suggestionRegenerateSub = this.aiChatService
            .regenerateSuggestions(target, customerId)
            .subscribe({
                next: (res) => {
                    this.aiSuggestionsRefreshing = false;
                    if (res.success && res.data?.suggestions?.length) {
                        const latest = [...this.aiConversation].reverse().find((m) => m.role === 'assistant');
                        if (latest) {
                            latest.suggestions = res.data.suggestions;
                        }
                    }
                    this.cdr.detectChanges();
                },
                error: () => {
                    this.aiSuggestionsRefreshing = false;
                    this.cdr.detectChanges();
                }
            });
    }

    /** Text content for an assistant message (for ngx-markdown): translated key or plain message. Normalizes newlines for proper markdown. */
    getAssistantMessageContent(item: { messageKey?: string; message: string }): string {
        const raw = item.messageKey ? this.translate.instant(item.messageKey) : (item.message || '');
        if (!raw) return '';
        return ComponentsComponent.normalizeAiMarkdownForDisplay(raw);
    }

    /**
     * Normalizes AI/streamed markdown that often arrives as one long line so GFM (marked) can render
     * lists, headings, and paragraphs. Covers common LLM patterns; order matters.
     *
     * Scenarios: atx headings after punctuation; **Title:** - bullets; colon + dash or numbered lists;
     * sentence-end + new numbered step; chained `1. … 2. …` on one line; parens/brackets + dash bullet;
     * • bullets; single-`*` bullets (not `**`); bold row ** - **; bold + EN/SV sentence openers;
     * horizontal rules; blockquotes after punctuation.
     */
    private static normalizeAiMarkdownForDisplay(text: string): string {
        let s = text
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .replace(/\\n/g, '\n');

        // Remove noisy leading "|" from streamed lines, while preserving real table rows.
        s = ComponentsComponent.cleanNoisyPipePrefixes(s);

        // If heading markers arrive inline (e.g. "Safetly: ### Check-In Process"),
        // move them to a new line so markdown parses them as headings.
        s = s.replace(/([^\n])\s+(#{1,6}\s+)/g, '$1\n$2');

        // Keep numbered points readable when AI sends them inline.
        s = s.replace(/:\s+(\d{1,2}\.\s+)/g, ':\n$1');
        for (let i = 0; i < 10; i++) {
            const next = s.replace(/(\d{1,2}\.\s+[^\n]+?)\s+(\d{1,2}\.\s+)/g, '$1\n$2');
            if (next === s) {
                break;
            }
            s = next;
        }

        // Keep inline bullets on separate lines.
        s = s.replace(/([^\n])\s+([*-])\s+(?=\S)/g, '$1\n$2 ');

        // --- Tidy excessive blank lines from chained replacements
        s = s.replace(/\n{3,}/g, '\n\n');

        return s.trim();
    }

    /**
     * Strips leading `|` noise from chunked plain-text responses but leaves valid
     * markdown tables unchanged.
     */
    private static cleanNoisyPipePrefixes(text: string): string {
        const lines = text.split('\n');
        const tableSeparator = /^\s*\|?\s*:?-{3,}:?(?:\s*\|\s*:?-{3,}:?)+\s*\|?\s*$/;
        const fullTableRow = /^\s*\|(?:[^|\n]*\|)+\s*[^|\n]*\|?\s*$/;
        const singlePipeCell = /^\s*\|\s*[^|]+$/;
        const pipeOnly = /^\s*\|\s*$/;

        const nonEmpty = lines.filter((l) => l.trim().length > 0);
        const noisyPipeCount = nonEmpty.filter((l) => singlePipeCell.test(l) || pipeOnly.test(l)).length;
        const hasLikelyNoise = nonEmpty.length > 0 && noisyPipeCount / nonEmpty.length >= 0.35;

        if (!hasLikelyNoise) {
            return text;
        }

        return lines
            .map((line) => {
                if (tableSeparator.test(line) || fullTableRow.test(line)) {
                    return line;
                }
                if (pipeOnly.test(line)) {
                    return '';
                }
                if (singlePipeCell.test(line)) {
                    return line.replace(/^\s*\|\s?/, '');
                }
                return line;
            })
            .join('\n');
    }

    /** Scroll the AI chat to the bottom so the latest message is in view (after send or after response). */
    private scrollAiChatToBottom(): void {
        const el = this.aiScrollContent?.nativeElement;
        if (!el) return;
        requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight;
        });
    }

    sendAiMessage(event?: KeyboardEvent): void {
        if (event && event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
        }

        const prompt = this.aiInput?.trim();
        if (!prompt || this.aiSending) {
            return;
        }

        this.aiError = null;
        this.aiConversation.push({ role: 'user', message: prompt, sentAt: new Date() });
        this.aiInput = '';
        this.aiConversation.push({ role: 'assistant', message: '', sentAt: new Date(), thinkingState: 'thinking' });
        this.aiStreamingMessageIndex = this.aiConversation.length - 1;
        this.cdr.detectChanges();
        this.scrollAiChatToBottom();

        this.aiSending = true;
        const customerId = this.customer?.id ?? null;
        const locale = this.currentLang || undefined;

        this.aiStreamSubscription?.unsubscribe();
        this.aiStreamSubscription = this.aiChatService.streamMessage(prompt, customerId, locale).subscribe({
            next: (streamEvent) => this.handleAiStreamEvent(streamEvent),
            error: (err) => this.handleAiStreamError(err),
            complete: () => this.handleAiStreamComplete()
        });
    }

    private handleAiStreamEvent(event: AiChatStreamEvent): void {
        const assistantIndex = this.aiStreamingMessageIndex ?? (this.aiConversation.length - 1);
        if (assistantIndex < 0 || assistantIndex >= this.aiConversation.length) {
            return;
        }

        const assistantMessage = this.aiConversation[assistantIndex];

        if (event.kind === 'start') {
            this.aiChatService.markConversationKnown();
            if (assistantMessage.thinkingState) {
                assistantMessage.thinkingState = 'generating';
            }
        } else if (event.kind === 'delta' && event.text) {
            this.aiChatService.markConversationKnown();
            assistantMessage.thinkingState = undefined;
            assistantMessage.message += event.text;
        } else if (event.kind === 'final') {
            this.aiChatService.markConversationKnown();
            assistantMessage.thinkingState = undefined;
            if (event.text) {
                assistantMessage.message = event.text;
            }
            if (event.data) {
                assistantMessage.provider = event.data.provider;
                assistantMessage.sources = event.data.sources;
                if (event.data.suggestions != null) {
                    assistantMessage.suggestions = event.data.suggestions;
                }
            }
        } else if (event.kind === 'error') {
            this.handleAiStreamError(event.error ?? null);
            return;
        } else if (event.kind === 'done') {
            this.finalizeStreamingAssistantMessage();
            return;
        }

        this.cdr.detectChanges();
        this.scrollAiChatToBottom();
    }

    private handleAiStreamError(err: unknown): void {
        const fallbackError = this.translate.instant('ai.error-generic');
        const errorMessage = err instanceof Error
            ? err.message || fallbackError
            : (typeof err === 'string' ? err : fallbackError);

        this.aiError = errorMessage;
        const assistantIndex = this.aiStreamingMessageIndex ?? (this.aiConversation.length - 1);
        if (assistantIndex >= 0 && assistantIndex < this.aiConversation.length) {
            const assistantMessage = this.aiConversation[assistantIndex];
            assistantMessage.thinkingState = undefined;
            assistantMessage.message = errorMessage;
            assistantMessage.sentAt = new Date();
        }

        this.aiStreamingMessageIndex = undefined;
        this.aiStreamSubscription = undefined;
        this.aiSending = false;
        this.cdr.detectChanges();
        this.scrollAiChatToBottom();
    }

    private handleAiStreamComplete(): void {
        if (this.aiStreamingMessageIndex != null && this.aiStreamingMessageIndex >= 0) {
            this.finalizeStreamingAssistantMessage();
            return;
        }

        this.aiSending = false;
        this.aiStreamSubscription = undefined;
        this.cdr.detectChanges();
    }

    private finalizeStreamingAssistantMessage(): void {
        const assistantIndex = this.aiStreamingMessageIndex;
        if (assistantIndex == null || assistantIndex < 0 || assistantIndex >= this.aiConversation.length) {
            this.aiStreamingMessageIndex = undefined;
            this.aiStreamSubscription = undefined;
            this.aiSending = false;
            return;
        }

        const assistantMessage = this.aiConversation[assistantIndex];
        assistantMessage.sentAt = new Date();
        this.aiStreamingMessageIndex = undefined;
        this.aiStreamSubscription = undefined;
        this.aiSending = false;
        this.cdr.detectChanges();
        this.scrollAiChatToBottom();
    }
}